import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";

const weekdays = [
  { id: "monday", label: "Lundi" },
  { id: "tuesday", label: "Mardi" },
  { id: "wednesday", label: "Mercredi" },
  { id: "thursday", label: "Jeudi" },
  { id: "friday", label: "Vendredi" },
  { id: "saturday", label: "Samedi" },
  { id: "sunday", label: "Dimanche" },
];

/**
 * Component for managing available weekdays
 */
const WeekdaysSettings = () => {
  const [availableWeekdays, setAvailableWeekdays] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({
    show: false,
    message: "",
    severity: "success",
  });

  // Fetch available weekdays on component mount
  useEffect(() => {
    const fetchWeekdays = async () => {
      setIsLoading(true);
      try {
        const weekdaysDoc = await getDoc(
          doc(db, "bus_available_weekdays", "available_weekdays")
        );
        if (weekdaysDoc.exists()) {
          setAvailableWeekdays(weekdaysDoc.data());
        } else {
          // Initialize with all days available if no document exists
          const initialWeekdays = {};
          weekdays.forEach((day) => {
            initialWeekdays[day.id] = true;
          });
          setAvailableWeekdays(initialWeekdays);
        }
      } catch (error) {
        console.error("Error fetching weekdays:", error);
        setSaveStatus({
          show: true,
          message: "Erreur lors du chargement des données",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeekdays();
  }, []);

  // Handle checkbox change
  const handleWeekdayChange = (weekdayId) => {
    setAvailableWeekdays((prev) => ({
      ...prev,
      [weekdayId]: !prev[weekdayId],
    }));
  };

  // Save weekdays to Firestore
  const handleSaveWeekdays = async () => {
    setIsSaving(true);
    setSaveStatus({ show: false, message: "", severity: "success" });

    try {
      await setDoc(
        doc(db, "bus_available_weekdays", "available_weekdays"),
        availableWeekdays
      );
      setSaveStatus({
        show: true,
        message: "Jours disponibles mis à jour avec succès",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving weekdays:", error);
      setSaveStatus({
        show: true,
        message: "Erreur lors de la sauvegarde des données",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
      // Hide the alert after 3 seconds
      if (saveStatus.show) {
        setTimeout(() => {
          setSaveStatus((prev) => ({ ...prev, show: false }));
        }, 3000);
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress sx={{ color: "#c84653" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3, color: "#676767" }}>
        Sélectionnez les jours de la semaine où les réservations seront
        disponibles:
      </Typography>

      <FormGroup>
        {weekdays.map((day) => (
          <FormControlLabel
            key={day.id}
            control={
              <Checkbox
                checked={availableWeekdays[day.id] || false}
                onChange={() => handleWeekdayChange(day.id)}
                color="primary"
                sx={{
                  "&.Mui-checked": {
                    color: "#c84653",
                  },
                }}
              />
            }
            label={day.label}
            sx={{
              mb: 1.5,
              "& .MuiFormControlLabel-label": {
                fontSize: "1rem",
                color: "#444349",
              },
            }}
          />
        ))}
      </FormGroup>

      <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}>
        {saveStatus.show && (
          <Alert severity={saveStatus.severity} sx={{ mb: 2 }}>
            {saveStatus.message}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveWeekdays}
          disabled={isSaving}
          sx={{
            backgroundColor: "#c84653",
            "&:hover": {
              backgroundColor: "#a93a46",
            },
            alignSelf: "flex-start",
            minWidth: "200px",
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          {isSaving
            ? "Sauvegarde en cours..."
            : "Enregistrer les modifications"}
        </Button>
      </Box>
    </Box>
  );
};

export default WeekdaysSettings;
